import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { API_ENDPOINTS } from '@/config/apiEndpoints';

/**
 * Fetches the current user's onboarding status.
 * Shared by: OnboardingRoleRouter, all 4 wizards, OnboardingStatusPage.
 */
export const useOnboardingStatus = (options = {}) => {
  return useQuery({
    queryKey: ['onboarding', 'status'],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.ONBOARDING_STATUS);
      return response?.data || null;
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 2;
    },
    ...options
  });
};

/**
 * Mutation: POST to set program context before loading wizard (StudentOnboardingWizard).
 */
export const useSetProgramContext = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post(API_ENDPOINTS.ONBOARDING_SET_PROGRAM_CONTEXT, {}, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] });
      options.onSuccess?.();
    },
    onError: (error) => {
      console.error('Error setting program context:', error);
      options.onError?.(error);
    },
    ...options
  });
};

/**
 * Mutation: POST to resolve a public onboarding email (OnboardingPublicPage).
 */
export const useResolveOnboarding = (options = {}) => {
  return useMutation({
    mutationFn: (data) =>
      apiClient.post(API_ENDPOINTS.PUBLIC_ONBOARDING_RESOLVE, {}, data),
    onError: (error) => {
      console.error('Error resolving onboarding:', error);
      options.onError?.(error);
    },
    ...options
  });
};

/**
 * Mutation: POST to start onboarding - create account + submission (OnboardingPublicPage).
 */
export const useStartOnboarding = (options = {}) => {
  return useMutation({
    mutationFn: (data) =>
      apiClient.post(API_ENDPOINTS.PUBLIC_ONBOARDING_START, {}, data),
    onError: (error) => {
      console.error('Error starting onboarding:', error);
      options.onError?.(error);
    },
    ...options
  });
};

// ─── Step-level hooks (INF-FQUA-13) ────────────────────────────────────────

/**
 * Fetches geography states for a given country code.
 * Only hits API for Mexico ('MX'); other countries use local data.
 * React Query deduplicates identical calls (e.g. CorporateDetailsStep commercial + fiscal both 'MX').
 */
export const useGeographyStates = (countryCode, options = {}) => {
  return useQuery({
    queryKey: ['geography', 'states', countryCode],
    queryFn: async () => {
      const response = await apiClient.get(
        API_ENDPOINTS.PUBLIC_GEOGRAPHY_STATES,
        { country_code: countryCode }
      );
      const items = Array.isArray(response?.data) ? response.data : [];
      return items.map((item) => ({
        value: item.state_name,
        label: item.state_name
      }));
    },
    enabled: countryCode === 'MX',
    staleTime: 30 * 60 * 1000,
    retry: 2,
    ...options
  });
};

/**
 * Mutation: PATCH identity data (IdentityStep, CorporateIdentityStep).
 */
export const useSaveIdentity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      apiClient.patch(API_ENDPOINTS.ONBOARDING_IDENTITY, {}, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] });
    }
  });
};

/**
 * Mutation: PATCH teacher details. Pass { partial: true } in the payload wrapper for draft saves.
 */
export const useSaveTeacherDetails = ({ partial = false } = {}) => {
  const queryClient = useQueryClient();
  const endpoint = partial
    ? API_ENDPOINTS.ONBOARDING_TEACHER_DETAILS_PARTIAL
    : API_ENDPOINTS.ONBOARDING_TEACHER_DETAILS;
  return useMutation({
    mutationFn: (payload) => apiClient.patch(endpoint, {}, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] });
    }
  });
};

/**
 * Mutation: PATCH admin details (partial save — AdminDetailsStep uses a single endpoint for both draft and submit).
 */
export const useSaveAdminDetails = ({ partial = false } = {}) => {
  const queryClient = useQueryClient();
  const endpoint = partial
    ? API_ENDPOINTS.ONBOARDING_ADMIN_DETAILS_PARTIAL
    : API_ENDPOINTS.ONBOARDING_ADMIN_DETAILS;
  return useMutation({
    mutationFn: (payload) => apiClient.patch(endpoint, {}, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] });
    }
  });
};

/**
 * Mutation: PATCH student details. partial=true for draft saves.
 */
export const useSaveStudentDetails = ({ partial = false } = {}) => {
  const queryClient = useQueryClient();
  const endpoint = partial
    ? API_ENDPOINTS.ONBOARDING_STUDENT_DETAILS_PARTIAL
    : API_ENDPOINTS.ONBOARDING_STUDENT_DETAILS;
  return useMutation({
    mutationFn: (payload) => apiClient.patch(endpoint, {}, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] });
    }
  });
};

/**
 * Mutation: PATCH corporate details (CorporateDetailsStep).
 */
export const useSaveCorporateDetails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      apiClient.patch(API_ENDPOINTS.ONBOARDING_CORPORATE_DETAILS_PARTIAL, {}, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] });
    }
  });
};

/**
 * Mutation: PATCH corporate operator contacts (CorporateOperatorStep).
 */
export const useSaveCorporateOperator = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      apiClient.patch(API_ENDPOINTS.ONBOARDING_CORPORATE_OPERATOR_PARTIAL, {}, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] });
    }
  });
};

/**
 * Mutation: POST upload document (shared by 5 step components).
 * Accepts { file: File, documentType: string }.
 */
export const useUploadDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, documentType }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      return apiClient.post(API_ENDPOINTS.ONBOARDING_UPLOAD_DOCUMENT, {}, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] });
    }
  });
};

/**
 * Mutation: DELETE document by type (shared by 5 step components).
 */
export const useDeleteDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentType) =>
      apiClient.delete(API_ENDPOINTS.ONBOARDING_DELETE_DOCUMENT, { document_type: documentType }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] });
    }
  });
};

/**
 * Mutation: PATCH document notes (TeacherDetailsStep, AdminDetailsStep).
 */
export const useSaveDocumentNotes = () => {
  return useMutation({
    mutationFn: (notes) =>
      apiClient.patch(API_ENDPOINTS.ONBOARDING_DOCUMENTS_NOTES, {}, { notes })
  });
};

/**
 * Mutation: POST submit onboarding (ReviewStep).
 */
export const useSubmitOnboarding = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post(API_ENDPOINTS.ONBOARDING_SUBMIT),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] });
    }
  });
};
